import type { ReactNode } from "react";
import { useEffect, useState } from "react";

/**
 * Глобальный ErrorBoundary — перехватывает необработанные ошибки рендера
 * и показывает сообщение вместо белого экрана.
 *
 * Использование:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 *
 * При падении дочернего компонента ErrorBoundary показывает сообщение
 * об ошибке и кнопку восстановления, чтобы пользователь не видел
 * пустой/белый экран.
 */
export const ErrorBoundary = ({ children }: { children: ReactNode }) => {
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  useEffect(() => {
    const handler = (event: Event) => {
      // Подавляем повторные срабатывания
      if (hasError) return;

      const errEvent = event as CustomEvent & { error?: Error };
      const message =
        errEvent.error?.message ??
        (typeof errEvent.detail === "string" ? errEvent.detail : null) ??
        "An unexpected error occurred. Please try reloading the page.";

      setHasError(true);
      setErrorMessage(message);
      event.preventDefault();
    };

    window.addEventListener("error", handler as EventListener);
    // Также ловим rejected promises, которые не обработаны
    window.addEventListener("unhandledrejection", handler as EventListener);

    return () => {
      window.removeEventListener("error", handler as EventListener);
      window.removeEventListener(
        "unhandledrejection",
        handler as EventListener,
      );
    };
  }, [hasError]);

  if (hasError) {
    return (
      <div
        style={{
          padding: "2rem",
          textAlign: "center",
          fontFamily: "system-ui, sans-serif",
        }}
      >
        <div
          style={{
            maxWidth: "480px",
            margin: "2rem auto",
            padding: "1.5rem",
            border: "1px solid #e0e0e0",
            borderRadius: "12px",
            background: "#fafafa",
          }}
        >
          <p style={{ fontSize: "40px", marginBottom: "0.5rem" }}>⚠️</p>
          <h2 style={{ margin: "0 0 0.75rem", fontSize: "18px" }}>
            Something went wrong
          </h2>
          <p
            style={{
              color: "#666",
              fontSize: "14px",
              lineHeight: "1.5",
              margin: "0 0 1rem",
            }}
          >
            {errorMessage}
          </p>
          <p style={{ color: "#999", fontSize: "12px", margin: "0 0 1rem" }}>
            If this persists, please contact support.
          </p>
          <button
            onClick={() => {
              setHasError(false);
              setErrorMessage(null);
              window.location.href = "/profile";
            }}
            style={{
              padding: "10px 24px",
              fontSize: "14px",
              fontWeight: 600,
              border: "none",
              borderRadius: "8px",
              background: "#1a1a2e",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            ← Back to Profile
          </button>
        </div>
      </div>
    );
  }

  return <>{children}</>;
};
